// src/pages/admin/AdminKyc.tsx
import React, { useState, useEffect } from "react";
import { GlobalStyles } from "../../styles/globalStyles";
import {
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  User,
  FileText,
  Shield,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  X,
  Image as ImageIcon,
  File,
  Loader2,
  Sparkles,
  Filter,
  ChevronDown,
  TrendingUp,
  UserCheck,
  AlertTriangle,
  Users,
  BarChart3,
  ShieldAlert,
  Check,
  X as XIcon,
} from "lucide-react";
import toast from "react-hot-toast";
import { kycService } from "../../services/api";
import LoadingScreen from "../../components/LoadingScreen";

interface KycSubmission {
  id: number;
  user_id: number;
  document_type: string;
  status: string;
  admin_notes: string;
  submitted_at: string;
  verified_at?: string;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    username: string;
    phone?: string;
    verification_level: number;
  };
  document_front_url?: string;
  document_back_url?: string;
  selfie_photo_url?: string;
}

interface PaginationData {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

// Modal Component
const KycDetailsModal: React.FC<{
  submission: KycSubmission | null;
  isOpen: boolean;
  onClose: () => void;
}> = ({ submission, isOpen, onClose }) => {
  if (!isOpen || !submission) return null;

  const userName = submission.user
    ? `${submission.user.first_name} ${submission.user.last_name}`
    : `User #${submission.user_id}`;
  const userEmail = submission.user?.email || "N/A";

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
        <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10">
                <Shield className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  KYC Submission Details
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  ID: {submission.id} • Submitted:{" "}
                  {new Date(submission.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[70vh]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-6">
                {/* User Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    User Information
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="p-2 rounded-lg bg-primary/10 mr-3">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {userName}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {userEmail}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          User ID
                        </p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {submission.user_id}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Verification Level
                        </p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          Level {submission.user?.verification_level || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Document Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Document Information
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 bg-blue-500/5 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <File className="h-4 w-4 text-blue-500" />
                        <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          Type
                        </p>
                      </div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {submission.document_type
                          .replace("_", " ")
                          .toUpperCase()}
                      </p>
                    </div>
                    <div className="p-4 bg-purple-500/5 border border-purple-200 dark:border-purple-800 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-4 w-4 text-purple-500" />
                        <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          Submitted
                        </p>
                      </div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {new Date(submission.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Status
                  </h4>
                  <div
                    className={`p-4 rounded-lg ${
                      submission.status === "approved"
                        ? "bg-emerald-500/10 border-emerald-200 dark:border-emerald-800"
                        : submission.status === "rejected"
                          ? "bg-rose-500/10 border-rose-200 dark:border-rose-800"
                          : "bg-amber-500/10 border-amber-200 dark:border-amber-800"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {submission.status === "approved" ? (
                          <CheckCircle className="h-5 w-5 text-emerald-500" />
                        ) : submission.status === "rejected" ? (
                          <XCircle className="h-5 w-5 text-rose-500" />
                        ) : (
                          <Clock className="h-5 w-5 text-amber-500" />
                        )}
                        <span className="font-medium text-gray-900 dark:text-white">
                          {submission.status.charAt(0).toUpperCase() +
                            submission.status.slice(1)}
                        </span>
                      </div>
                      {submission.verified_at && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(
                            submission.verified_at,
                          ).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Admin Notes */}
                {submission.admin_notes && (
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Admin Notes
                    </h4>
                    <div className="p-4 bg-yellow-500/5 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {submission.admin_notes}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Document Images */}
              <div className="space-y-6">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  Document Images
                </h4>

                {/* Front Document */}
                <div className="space-y-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Document Front
                  </p>
                  {submission.document_front_url ? (
                    <div className="relative group">
                      <img
                        src={submission.document_front_url}
                        alt="Document Front"
                        className="w-full h-48 object-cover rounded-lg border border-gray-200 dark:border-gray-800"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "https://via.placeholder.com/400x300?text=Image+Not+Available";
                        }}
                      />
                      <a
                        href={submission.document_front_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity rounded-lg"
                      >
                        <span className="px-4 py-2 bg-white/90 dark:bg-gray-800/90 rounded-lg text-sm font-medium">
                          View Full Size
                        </span>
                      </a>
                    </div>
                  ) : (
                    <div className="w-full h-48 flex items-center justify-center bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-800">
                      <div className="text-center">
                        <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Not available</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Back Document */}
                {submission.document_back_url && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Document Back
                    </p>
                    <div className="relative group">
                      <img
                        src={submission.document_back_url}
                        alt="Document Back"
                        className="w-full h-48 object-cover rounded-lg border border-gray-200 dark:border-gray-800"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "https://via.placeholder.com/400x300?text=Image+Not+Available";
                        }}
                      />
                      <a
                        href={submission.document_back_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity rounded-lg"
                      >
                        <span className="px-4 py-2 bg-white/90 dark:bg-gray-800/90 rounded-lg text-sm font-medium">
                          View Full Size
                        </span>
                      </a>
                    </div>
                  </div>
                )}

                {/* Selfie Photo */}
                <div className="space-y-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Selfie Photo
                  </p>
                  {submission.selfie_photo_url ? (
                    <div className="relative group">
                      <img
                        src={submission.selfie_photo_url}
                        alt="Selfie"
                        className="w-full h-48 object-cover rounded-lg border border-gray-200 dark:border-gray-800"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "https://via.placeholder.com/400x300?text=Image+Not+Available";
                        }}
                      />
                      <a
                        href={submission.selfie_photo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity rounded-lg"
                      >
                        <span className="px-4 py-2 bg-white/90 dark:bg-gray-800/90 rounded-lg text-sm font-medium">
                          View Full Size
                        </span>
                      </a>
                    </div>
                  ) : (
                    <div className="w-full h-48 flex items-center justify-center bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-800">
                      <div className="text-center">
                        <User className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Not available</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end p-6 border-t border-gray-200 dark:border-gray-800">
            <button
              onClick={onClose}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline}`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Status Update Modal
const StatusUpdateModal: React.FC<{
  submission: KycSubmission | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (status: string, notes: string) => Promise<void>;
  isLoading: boolean;
}> = ({ submission, isOpen, onClose, onConfirm, isLoading }) => {
  const [status, setStatus] = useState<string>("approved");
  const [notes, setNotes] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !submission) return null;

  const userName = submission.user
    ? `${submission.user.first_name} ${submission.user.last_name}`
    : `User #${submission.user_id}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onConfirm(status, notes);
      setNotes("");
      onClose();
    } catch (error) {
      // Error is handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
        <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-md">
          <div className="p-6 border-b border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Update KYC Status
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Updating status for: {userName}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-3`}>
                Status
              </label>
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setStatus("approved")}
                  className={`flex items-center px-4 py-2 rounded-lg border ${
                    status === "approved"
                      ? "bg-emerald-500/10 border-emerald-500 text-emerald-500"
                      : "border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-emerald-500"
                  }`}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => setStatus("rejected")}
                  className={`flex items-center px-4 py-2 rounded-lg border ${
                    status === "rejected"
                      ? "bg-rose-500/10 border-rose-500 text-rose-500"
                      : "border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-rose-500"
                  }`}
                >
                  <XIcon className="h-4 w-4 mr-2" />
                  Reject
                </button>
              </div>
            </div>

            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Admin Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this KYC decision..."
                rows={3}
                disabled={isSubmitting || isLoading}
                className={GlobalStyles.input}
              />
            </div>

            {status === "rejected" && (
              <div className="p-4 bg-amber-500/5 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Rejecting will reset the user's verification level.
                  </p>
                </div>
              </div>
            )}
          </form>

          <div className="flex items-center justify-end p-6 border-t border-gray-200 dark:border-gray-800 gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${
                status === "approved"
                  ? GlobalStyles.buttonSuccess
                  : GlobalStyles.buttonError
              }`}
            >
              {isSubmitting || isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : status === "approved" ? (
                <CheckCircle className="h-4 w-4 mr-2" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              {status === "approved" ? "Approve" : "Reject"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminKyc: React.FC = () => {
  const [kycSubmissions, setKycSubmissions] = useState<KycSubmission[]>([]);
  const [filter, setFilter] = useState({
    status: "",
    document_type: "",
    search: "",
  });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationData>({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  });

  const [selectedSubmission, setSelectedSubmission] =
    useState<KycSubmission | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string>("");

  useEffect(() => {
    fetchKycSubmissions();
  }, [filter.status, filter.document_type, pagination.current_page]);

  const fetchKycSubmissions = async () => {
    setLoading(true);
    try {
      const params: any = {
        status: filter.status || undefined,
        document_type: filter.document_type || undefined,
        page: pagination.current_page,
      };

      const response = await kycService.getAllKyc(params);

      if (response.data && Array.isArray(response.data)) {
        setKycSubmissions(response.data);
        if (response.meta) {
          setPagination({
            current_page: response.meta.current_page || 1,
            last_page: response.meta.last_page || 1,
            per_page: response.meta.per_page || 20,
            total: response.meta.total || 0,
          });
        }
      } else if (Array.isArray(response)) {
        setKycSubmissions(response);
        setPagination({
          current_page: 1,
          last_page: 1,
          per_page: response.length,
          total: response.length,
        });
      } else {
        const data =
          response.data || response.items || response.submissions || [];
        setKycSubmissions(Array.isArray(data) ? data : []);
        if (response.meta || response.pagination) {
          const meta = response.meta || response.pagination || {};
          setPagination({
            current_page: meta.current_page || 1,
            last_page: meta.last_page || 1,
            per_page: meta.per_page || 20,
            total: meta.total || data.length || 0,
          });
        }
      }
    } catch (error: any) {
      console.error("Failed to fetch KYC submissions:", error);
      toast.error(
        error.response?.data?.error || "Failed to load KYC submissions",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (submission: KycSubmission) => {
    fetchKycDetails(submission.id);
  };

  const fetchKycDetails = async (id: number) => {
    try {
      setUpdatingId(id);
      const response = await kycService.getKycDetails(id);
      setSelectedSubmission(response);
      setDetailsModalOpen(true);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to load KYC details");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleStatusUpdateClick = (submission: KycSubmission) => {
    setSelectedSubmission(submission);
    setStatusModalOpen(true);
  };

  const handleStatusUpdate = async (status: string, notes: string) => {
    if (!selectedSubmission) return;

    try {
      setUpdatingStatus(status);
      await kycService.updateKycStatus(selectedSubmission.id, {
        status,
        admin_notes: notes || undefined,
      });

      setKycSubmissions((prev) =>
        prev.map((submission) =>
          submission.id === selectedSubmission.id
            ? {
                ...submission,
                status,
                admin_notes: notes || submission.admin_notes,
                verified_at:
                  status === "approved"
                    ? new Date().toISOString()
                    : submission.verified_at,
                user: submission.user
                  ? {
                      ...submission.user,
                      verification_level: status === "approved" ? 3 : 1,
                    }
                  : submission.user,
              }
            : submission,
        ),
      );

      toast.success(`KYC ${status} successfully`);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to update KYC status");
      throw error;
    } finally {
      setUpdatingStatus("");
      setSelectedSubmission(null);
    }
  };

  const handleRefresh = () => {
    fetchKycSubmissions();
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.last_page) {
      setPagination((prev) => ({ ...prev, current_page: page }));
    }
  };

  const handleClearFilters = () => {
    setFilter({
      status: "",
      document_type: "",
      search: "",
    });
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  // Calculate statistics
  const pendingCount = kycSubmissions.filter(
    (s) => s.status === "pending",
  ).length;
  const approvedCount = kycSubmissions.filter(
    (s) => s.status === "approved",
  ).length;
  const rejectedCount = kycSubmissions.filter(
    (s) => s.status === "rejected",
  ).length;
  const totalCount = kycSubmissions.length;

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      pagination.current_page - Math.floor(maxVisiblePages / 2),
    );
    let endPage = Math.min(
      pagination.last_page,
      startPage + maxVisiblePages - 1,
    );

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
            pagination.current_page === i
              ? "bg-primary text-white"
              : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
          }`}
        >
          {i}
        </button>,
      );
    }

    return pages;
  };

  const filteredSubmissions = kycSubmissions.filter((submission) => {
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      const userName = submission.user
        ? `${submission.user.first_name || ""} ${submission.user.last_name || ""}`.toLowerCase()
        : "";
      const userEmail = submission.user?.email?.toLowerCase() || "";
      const userId = submission.user_id.toString();

      return (
        userName.includes(searchLower) ||
        userEmail.includes(searchLower) ||
        userId.includes(searchLower) ||
        submission.document_type.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  if (loading && kycSubmissions.length === 0) {
    return <LoadingScreen message="Loading KYC submissions..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            KYC Management
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Review and manage KYC submissions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleClearFilters}
            className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline}`}
          >
            Clear Filters
          </button>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} disabled:opacity-50`}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={`${GlobalStyles.card}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Users className="h-5 w-5 text-blue-500" />
            </div>
            <TrendingUp className="h-5 w-5 text-emerald-500" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            Total Submissions
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {totalCount}
          </p>
        </div>

        <div className={`${GlobalStyles.card}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Clock className="h-5 w-5 text-amber-500" />
            </div>
            <span
              className={`${GlobalStyles.badge} ${GlobalStyles.badgeWarning}`}
            >
              Pending
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            Pending Review
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {pendingCount}
          </p>
        </div>

        <div className={`${GlobalStyles.card}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
            </div>
            <span
              className={`${GlobalStyles.badge} ${GlobalStyles.badgeSuccess}`}
            >
              Approved
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            Approved
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {approvedCount}
          </p>
        </div>

        <div className={`${GlobalStyles.card}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 rounded-lg bg-rose-500/10">
              <XCircle className="h-5 w-5 text-rose-500" />
            </div>
            <span
              className={`${GlobalStyles.badge} ${GlobalStyles.badgeError}`}
            >
              Rejected
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            Rejected
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {rejectedCount}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className={`${GlobalStyles.card}`}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              className={GlobalStyles.input}
            />
          </div>

          <div className="relative">
            <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className={GlobalStyles.input}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="relative">
            <File className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <select
              value={filter.document_type}
              onChange={(e) =>
                setFilter({ ...filter, document_type: e.target.value })
              }
              className={GlobalStyles.input}
            >
              <option value="">All Documents</option>
              <option value="passport">Passport</option>
              <option value="id_card">ID Card</option>
              <option value="driver_license">Driver License</option>
            </select>
          </div>

          <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center justify-between">
            <span>
              {filteredSubmissions.length} of {totalCount} submissions
            </span>
          </div>
        </div>
      </div>

      {/* KYC Table */}
      <div className={`${GlobalStyles.card} !p-0 overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className={GlobalStyles.table}>
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Document
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Submitted
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {filteredSubmissions.length > 0 ? (
                filteredSubmissions.map((submission) => {
                  const userName = submission.user
                    ? `${submission.user.first_name || ""} ${submission.user.last_name || ""}`.trim() ||
                      `User #${submission.user_id}`
                    : `User #${submission.user_id}`;
                  const userEmail = submission.user?.email || "N/A";

                  return (
                    <tr
                      key={submission.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                            <User className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {userName}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {userEmail}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`${GlobalStyles.badge}`}>
                          {submission.document_type
                            .replace("_", " ")
                            .toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {submission.status === "approved" ? (
                            <CheckCircle className="h-4 w-4 text-emerald-500 mr-2" />
                          ) : submission.status === "rejected" ? (
                            <XCircle className="h-4 w-4 text-rose-500 mr-2" />
                          ) : (
                            <Clock className="h-4 w-4 text-amber-500 mr-2" />
                          )}
                          <span
                            className={`${GlobalStyles.badge} ${
                              submission.status === "approved"
                                ? GlobalStyles.badgeSuccess
                                : submission.status === "rejected"
                                  ? GlobalStyles.badgeError
                                  : GlobalStyles.badgeWarning
                            }`}
                          >
                            {submission.status.charAt(0).toUpperCase() +
                              submission.status.slice(1)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(submission.created_at).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewDetails(submission)}
                            disabled={updatingId === submission.id}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg disabled:opacity-50"
                          >
                            {updatingId === submission.id ? (
                              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                            ) : (
                              <Eye className="h-4 w-4 text-blue-500" />
                            )}
                          </button>

                          {submission.status === "pending" && (
                            <>
                              <button
                                onClick={() =>
                                  handleStatusUpdateClick(submission)
                                }
                                disabled={updatingStatus !== ""}
                                className="p-2 hover:bg-emerald-500/10 rounded-lg disabled:opacity-50"
                              >
                                {updatingStatus === "approved" &&
                                selectedSubmission?.id === submission.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />
                                ) : (
                                  <Check className="h-4 w-4 text-emerald-500" />
                                )}
                              </button>
                              <button
                                onClick={() =>
                                  handleStatusUpdateClick(submission)
                                }
                                disabled={updatingStatus !== ""}
                                className="p-2 hover:bg-rose-500/10 rounded-lg disabled:opacity-50"
                              >
                                {updatingStatus === "rejected" &&
                                selectedSubmission?.id === submission.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-rose-500" />
                                ) : (
                                  <XIcon className="h-4 w-4 text-rose-500" />
                                )}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="text-center">
                      <ShieldAlert className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        No KYC submissions found
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400">
                        {kycSubmissions.length === 0
                          ? "No KYC submissions have been made yet."
                          : "Try adjusting your filters"}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="flex flex-col md:flex-row items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-800 gap-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Showing{" "}
              <span className="font-medium">
                {Math.min(
                  (pagination.current_page - 1) * pagination.per_page + 1,
                  pagination.total,
                )}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(
                  pagination.current_page * pagination.per_page,
                  pagination.total,
                )}
              </span>{" "}
              of <span className="font-medium">{pagination.total}</span>{" "}
              submissions
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1 || loading}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              {renderPagination()}

              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={
                  pagination.current_page === pagination.last_page || loading
                }
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <KycDetailsModal
        submission={selectedSubmission}
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedSubmission(null);
        }}
      />

      <StatusUpdateModal
        submission={selectedSubmission}
        isOpen={statusModalOpen}
        onClose={() => {
          setStatusModalOpen(false);
          setSelectedSubmission(null);
        }}
        onConfirm={handleStatusUpdate}
        isLoading={updatingStatus !== ""}
      />
    </div>
  );
};

export default AdminKyc;
